/* global axios */
import ApiClient from './ApiClient';

export const buildContactParams = (page, sortAttr, label, search) => {
  let params = `include_contact_inboxes=false&page=${page}&sort=${sortAttr}`;
  if (search) {
    params = `${params}&q=${search}`;
  }
  if (label) {
    params = `${params}&labels[]=${label}`;
  }
  return params;
};

class ContactAPI extends ApiClient {
  constructor() {
    super('contacts', { accountScoped: true });
  }

  get(page, sortAttr = 'name', label = '') {
    let requestURL = `${this.url}?${buildContactParams(
      page,
      sortAttr,
      label,
      ''
    )}`;
    return axios.get(requestURL);
  }

  getConversations(contactId) {
    return axios.get(`${this.url}/${contactId}/conversations`);
  }

  getContactableInboxes(contactId) {
    return axios.get(`${this.url}/${contactId}/contactable_inboxes`);
  }

  getContactLabels(contactId) {
    return axios.get(`${this.url}/${contactId}/labels`);
  }

  updateContactLabels(contactId, labels) {
    return axios.post(`${this.url}/${contactId}/labels`, { labels });
  }

  search(search = '', page = 1, sortAttr = 'name', label = '') {
    let requestURL = `${this.url}/search?${buildContactParams(
      page,
      sortAttr,
      label,
      search
    )}`;
    return axios.get(requestURL);
  }

  // eslint-disable-next-line default-param-last
  filter(page = 1, sortAttr = 'name', queryPayload) {
    let requestURL = `${this.url}/filter?${buildContactParams(page, sortAttr)}`;
    return axios.post(requestURL, queryPayload);
  }

  importContacts(file) {
    const formData = new FormData();
    formData.append('import_file', file);
    return axios.post(`${this.url}/import`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  }

  importValidate(file) {
    const formData = new FormData();
    formData.append('import_file', file);
    return axios.post(`${this.url}/import_validate`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  }

  destroyCustomAttributes(contactId, customAttributes) {
    return axios.post(`${this.url}/${contactId}/destroy_custom_attributes`, {
      custom_attributes: customAttributes,
    });
  }

  destroyAvatar(contactId) {
    return axios.delete(`${this.url}/${contactId}/avatar`);
  }

  getStages(contactId) {
    return axios.get(`${this.url}/${contactId}/stages`);
  }

  // Valor y moneda del contacto en un pipeline. `expected` es lo que se veía
  // antes de editar: si otro usuario lo cambió, el backend responde 409.
  updateDeal(contactId, { pipelineId, dealValue, dealCurrency, expected }) {
    return axios.patch(`${this.url}/${contactId}/stages/deal`, {
      pipeline_id: pipelineId,
      deal_value: dealValue,
      deal_currency: dealCurrency,
      expected_deal_value: expected?.dealValue ?? null,
      expected_deal_currency: expected?.dealCurrency ?? null,
    });
  }

  // `expectedContactStageId`: la fila activa que se veía; si otro la cambió,
  // el backend responde 409 sin mover (ficha del negocio). Sin opciones, igual
  // que antes.
  // `lostReasonId` / `lostNote` (P10): motivo de pérdida al mover a una etapa
  // perdida; sin motivo, el CRM lo guarda como "sin motivo".
  // `pauseAiMoves` (E06): además de mover, pausa los movimientos de la IA para
  // el contacto en ese pipeline.
  moveToStage(
    contactId,
    stageId,
    { expectedContactStageId, notes, lostReasonId, lostNote, pauseAiMoves } = {}
  ) {
    return axios.post(`${this.url}/${contactId}/stages/move`, {
      stage_id: stageId,
      ...(expectedContactStageId !== undefined
        ? { expected_contact_stage_id: expectedContactStageId }
        : {}),
      ...(notes ? { notes } : {}),
      ...(lostReasonId ? { lost_reason_id: lostReasonId } : {}),
      ...(lostNote ? { lost_note: lostNote } : {}),
      ...(pauseAiMoves ? { pause_ai_moves: true } : {}),
    });
  }

  // Cambia el motivo de pérdida del contacto que ya está en una etapa perdida.
  // `lostReasonId` null = "sin motivo". 409 si el contacto se movió en el medio.
  updateLostReason(
    contactId,
    { pipelineId, expectedContactStageId, lostReasonId, lostNote }
  ) {
    return axios.patch(`${this.url}/${contactId}/stages/lost_reason`, {
      pipeline_id: pipelineId,
      expected_contact_stage_id: expectedContactStageId,
      lost_reason_id: lostReasonId || null,
      lost_note: lostNote || null,
    });
  }

  // Pausa / reanuda los movimientos de la IA del contacto en un pipeline (E06).
  pauseAiMoves(contactId, { pipelineId, reason } = {}) {
    return axios.post(`${this.url}/${contactId}/stages/pause_ai`, {
      pipeline_id: pipelineId,
      ...(reason ? { reason } : {}),
    });
  }

  resumeAiMoves(contactId, { pipelineId, reason } = {}) {
    return axios.post(`${this.url}/${contactId}/stages/resume_ai`, {
      pipeline_id: pipelineId,
      ...(reason ? { reason } : {}),
    });
  }

  // Ficha del negocio (P7): el contacto en un pipeline, en una respuesta.
  getDealSheet(contactId, pipelineId) {
    return axios.get(`${this.url}/${contactId}/stages/sheet`, {
      params: { pipeline_id: pipelineId },
    });
  }

  // Movimientos de etapa, del más reciente al más viejo, paginados.
  getStageHistory(contactId, { pipelineId, page = 1, perPage = 20 } = {}) {
    return axios.get(`${this.url}/${contactId}/stages/history`, {
      params: {
        ...(pipelineId ? { pipeline_id: pipelineId } : {}),
        page,
        per_page: perPage,
      },
    });
  }

  exportContacts(queryPayload) {
    return axios.post(`${this.url}/export`, queryPayload);
  }

  bulkDelete(contactIds) {
    return axios.post(`${this.url}/bulk_delete`, {
      contact_ids: contactIds,
    });
  }

  bulkLabel(contactIds, labels) {
    return axios.post(`${this.url}/bulk_label`, {
      contact_ids: contactIds,
      labels,
    });
  }
}

export default new ContactAPI();
